import React from 'react';

import { Box, TextField, Typography } from '@mui/material';

import { FooterConfig } from '../../../App';

interface FooterEditorProps {
  config: FooterConfig;
  onChange: (newConfig: FooterConfig) => void;
}

const FooterEditor: React.FC<FooterEditorProps> = ({ config, onChange }) => {
  const handleChange = (key: keyof FooterConfig, value: any) => {
    onChange({ ...config, [key]: value });
  };

  const handleLinkChange = (key: string, value: string) => {
    onChange({
      ...config,
      links: { ...config.links, [key]: value },
    });
  };

  const handleCustomLinkChange = (
    index: number,
    field: 'title' | 'url',
    value: string
  ) => {
    const newCustomLinks = [...(config.customLinks || [])];
    // Ensure array is filled up to index
    for (let i = 0; i <= index; i++) {
      if (!newCustomLinks[i]) {
        newCustomLinks[i] = { title: '', url: '' };
      }
    }

    newCustomLinks[index] = { ...newCustomLinks[index], [field]: value };

    // Filter out empty links (optional, but good for cleanup)?
    // Actually, preserving empty slots might be better for the UI interaction unless we want to clean up on save.
    // For now, let's just update the state.

    onChange({ ...config, customLinks: newCustomLinks });
  };

  return (
    <Box sx={{ display: 'flex', gap: 4, p: 2, alignItems: 'flex-start' }}>
      {/* Left Column */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Box
          sx={{
            border: '1px solid #ddd',
            p: 2,
            borderRadius: 1,
            bgcolor: 'background.paper',
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
            TEXT BEFORE LINKS
          </Typography>
          <TextField
            label="H6"
            fullWidth
            value={config.title || ''}
            onChange={(e) => handleChange('title', e.target.value)}
            sx={{ mb: 2 }}
            size="small"
          />
          <TextField
            label="Body 1"
            fullWidth
            value={config.subtitle || ''}
            onChange={(e) => handleChange('subtitle', e.target.value)}
            size="small"
          />
        </Box>

        <Box
          sx={{
            border: '1px solid #ddd',
            p: 2,
            borderRadius: 1,
            bgcolor: 'background.paper',
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
            TEXT AFTER LINKS
          </Typography>
          <TextField
            label="Body 1"
            fullWidth
            value={config.bottomText || ''}
            onChange={(e) => handleChange('bottomText', e.target.value)}
            size="small"
          />
        </Box>

        <Box
          sx={{
            border: '1px solid #ddd',
            p: 2,
            borderRadius: 1,
            bgcolor: 'background.paper',
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
            SOCIAL MEDIA
          </Typography>
          {['instagram', 'linkedin', 'facebook', 'youtube', 'pinterest'].map(
            (social) => (
              <TextField
                key={social}
                label={social.charAt(0).toUpperCase() + social.slice(1)}
                fullWidth
                value={
                  config.links?.[social as keyof typeof config.links] || ''
                }
                onChange={(e) => handleLinkChange(social, e.target.value)}
                sx={{ mb: 2 }}
                size="small"
              />
            )
          )}
        </Box>
      </Box>

      {/* Right Column */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
        {[0, 1, 2, 3].map((index) => (
          <Box
            key={index}
            sx={{
              border: '1px solid #ddd',
              p: 2,
              borderRadius: 1,
              bgcolor: 'background.paper',
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
              LINK {index + 1}
            </Typography>
            <TextField
              label="Title (H6)"
              fullWidth
              value={config.customLinks?.[index]?.title || ''}
              onChange={(e) =>
                handleCustomLinkChange(index, 'title', e.target.value)
              }
              sx={{ mb: 2 }}
              size="small"
            />
            <TextField
              label="Link"
              fullWidth
              value={config.customLinks?.[index]?.url || ''}
              onChange={(e) =>
                handleCustomLinkChange(index, 'url', e.target.value)
              }
              size="small"
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default FooterEditor;
